import { Component, OnInit, inject } from '@angular/core';
import {StatusBar, Style} from '@capacitor/status-bar';
import {Capacitor} from '@capacitor/core';
import {ConnectionStatus, Network} from '@capacitor/network';
import {AlertController, ModalController, NavController, Platform} from '@ionic/angular';
import {ToastService} from "./services/toast/toast.service";
import {SplashScreen} from "@capacitor/splash-screen";
import {App, AppState} from "@capacitor/app";
import {ActionPerformed, PushNotifications} from "@capacitor/push-notifications";
import {ScreenOrientation} from "@capacitor/screen-orientation";
import {Router} from "@angular/router";
import {BehaviorSubject} from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import {ForceUpdateService} from './services/force-update/force-update.service';
import {ForceUpdateModalComponent} from './shared/force-update-modal/force-update-modal.component';
import {CrashlyticsService} from './services/crashlytics.service';
import { DomSecurityHardeningService } from './services/security/dom-security-hardening.service';
import { DebtNotificationRouterService } from './services/debt-notification-router.service';
import { TrustedResourceService } from './services/security/trusted-resource.service';
import { LanguageService } from './services/language.service';
import { CdnTranslateLoader } from './services/cdn-translate-loader';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { LanguagePickerModalComponent } from './shared/language-picker-modal/language-picker-modal.component';
import { PwaInstallService } from './services/pwa-install.service';
import { PwaInstallPromptModalComponent } from './shared/pwa-install-prompt-modal/pwa-install-prompt-modal.component';
import { AuthService } from './services/auth/auth.service';
import { ConnectivityService } from './services/connectivity.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  private readonly customDeepLinkScheme = 'za.co.bitbridge.fas.finance.mobiweb';
  private readonly universalLinkHosts = ['m.billing.bitbridge.co.za', 'billing.bitbridge.co.za', 'api.bitbridge.co.za'];

  message: any = null;
  channel!: BroadcastChannel;

  /** Tracks the count of unread push notifications received during this session. */
  readonly unreadNotificationCount$ = new BehaviorSubject<number>(0);

  private readonly platform: Platform
  private readonly toastService: ToastService;
  private readonly navController: NavController;
  private readonly router: Router;
  private readonly alertController: AlertController;
  private readonly modalController: ModalController;
  private readonly forceUpdateService: ForceUpdateService;
  private readonly crashlyticsService: CrashlyticsService;
  private readonly domSecurityHardeningService: DomSecurityHardeningService;
  private readonly trustedResourceService: TrustedResourceService;
  private readonly debtNotificationRouter = inject(DebtNotificationRouterService);
  private readonly languageService: LanguageService;
  private readonly pwaInstallService: PwaInstallService;
  private readonly authService: AuthService;
  private readonly connectivityService: ConnectivityService;

  constructor(platform: Platform, toastService: ToastService, navController: NavController, router: Router, alertController: AlertController, modalController: ModalController, forceUpdateService: ForceUpdateService, crashlyticsService: CrashlyticsService, domSecurityHardeningService: DomSecurityHardeningService, trustedResourceService: TrustedResourceService, languageService: LanguageService, pwaInstallService: PwaInstallService, authService: AuthService, connectivityService: ConnectivityService, translateLoader: TranslateLoader, translateService: TranslateService) {
    this.platform = platform;
    this.toastService = toastService;
    this.navController = navController;
    this.router = router;
    this.alertController = alertController;
    this.modalController = modalController;
    this.forceUpdateService = forceUpdateService;
    this.crashlyticsService = crashlyticsService;
    this.domSecurityHardeningService = domSecurityHardeningService;
    this.trustedResourceService = trustedResourceService;
    this.languageService = languageService;
    this.pwaInstallService = pwaInstallService;
    this.authService = authService;
    this.connectivityService = connectivityService;
    // Give the loader a reference to TranslateService so background CDN
    // refreshes can trigger a silent reloadLang() when translations change.
    (translateLoader as CdnTranslateLoader).translateService = translateService;
  }

  async ngOnInit(): Promise<void> {
    document.body.classList.remove('theme-dark', 'theme-light');
    this.domSecurityHardeningService.start();
    // Previously only scheduled via PublicGuard (i.e. only when the app happened to land on a
    // public route first) — meaning a session resumed directly on a secure route never got the
    // silent-refresh timer (or the session-expiry countdown) started at all. No-ops safely when
    // there's no stored session.
    void this.authService.initTokenRefresh();
    await this.languageService.initialize();
    if (this.languageService.isFirstLaunch) {
      const langModal = await this.modalController.create({
        component: LanguagePickerModalComponent,
        componentProps: {
          selectedCode: this.languageService.currentLanguage,
          isFirstLaunch: true,
        },
        breakpoints: [0, 0.85, 1],
        initialBreakpoint: 0.85,
        backdropDismiss: false,
        cssClass: 'language-picker-modal',
      });
      await langModal.present();
      const { data } = await langModal.onWillDismiss();
      if (data?.code) {
        await this.languageService.setLanguage(data.code);
      }
    }
    await this.handleNetworkStatusChangeEvent();
    await this.handleAppInitialize();
    if (!Capacitor.isNativePlatform()) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.channel = new window.BroadcastChannel("bg-notification");
      this.channel.addEventListener('message', this.handlePwaNotificationMessage);
      navigator.serviceWorker?.addEventListener('message', this.handleServiceWorkerMessage);
      void this.maybeShowInstallPrompt();
    }
  }

  /**
   * Offers to install the PWA — a real native prompt on Android/Chrome/Edge (via the
   * `beforeinstallprompt` event PwaInstallService captured at app boot), or a manual
   * "Add to Home Screen" guide on iOS Safari, which has no install API at all. Never shown
   * if already installed (standalone display mode) or already prompted once before.
   */
  private async maybeShowInstallPrompt(): Promise<void> {
    // Let the app settle (and give Chrome a moment to fire beforeinstallprompt) before interrupting the user.
    setTimeout(async () => {
      if (!(await this.pwaInstallService.shouldOfferInstall())) {
        return;
      }
      const modal = await this.modalController.create({
        component: PwaInstallPromptModalComponent,
        componentProps: { canInstallDirectly: this.pwaInstallService.canPromptInstall() },
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        canDismiss: true,
        cssClass: 'pwa-install-prompt-modal',
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      await this.pwaInstallService.markPrompted();
      if (data?.action === 'install') {
        await this.pwaInstallService.promptInstall();
      }
    }, 2500);
  }

  async handleAppInitialize() {
    this.platform.ready().then(async () => {
      // ── Force-update gate: runs before splash hides so nothing else is reachable ──
      try {
        const versionInfo = await this.forceUpdateService.check();
        if (versionInfo.forceUpdate) {
          const modal = await this.modalController.create({
            component: ForceUpdateModalComponent,
            componentProps: { versionInfo },
            backdropDismiss: false,      // user cannot swipe/tap to close
            keyboardClose: false,        // hardware back (Android) cannot close it
            cssClass: 'force-update-modal',
          });
          await modal.present();
          // Deliberately NOT awaiting modal.onDidDismiss() — the modal is permanent
          // until the user exits the app from the store.
        }
      } catch (e) {
        // Never block app init on a failed version check
        console.warn('Force-update check failed:', e);
      }

      if (Capacitor.isNativePlatform()) {
        await SplashScreen.hide();
        // Capacitor's Style names the BACKGROUND, not the content: Style.Light = dark icons/clock/
        // battery for a light background (iOS .darkContent); Style.Dark = white content
        // (.lightContent). This app is always light-themed (see the hardcoded theme classes above)
        // on a white status bar, so it must be Style.Light — Style.Dark drew white-on-white and the
        // time, battery and notch area all disappeared.
        await StatusBar.setStyle({ style: Style.Light });
        await ScreenOrientation.lock({ orientation: 'portrait' });
        // Apply the user's crash-reporting preference as early as possible
        await this.crashlyticsService.applyStoredPreference();
      }
    });

    await App.addListener('backButton', () => {
      this.navController.back();
    });

    if (Capacitor.isNativePlatform()) {
      await this.handleMessage();
      await this.handleAppStateChange();
      await this.handleDeepLinks();
    }
  }

  async handleDeepLinks() {
    await App.addListener('appUrlOpen', async ({ url }) => {
      await this.navigateFromAppUrl(url);
    });

    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      await this.navigateFromAppUrl(launchUrl.url);
    }
  }

  async revalidateSession(): Promise<void> {
    if (await this.authService.hasStoredSession()) {
      await this.authService.initTokenRefresh();
    }
  }

  handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === 'visible') {
      await this.revalidateSession();
    }
  };

  async handleAppStateChange() {
    await App.addListener('appStateChange', async (state: AppState) => {
      if (state.isActive) {
        await this.revalidateSession();
        await PushNotifications.removeAllDeliveredNotifications();
        this.unreadNotificationCount$.next(0);
      }
    });
  }

  async handleNetworkStatusChangeEvent() {
    // Check the current state immediately too — a user who opens the app already offline would
    // otherwise get no warning at all until connectivity happens to change during this session.
    if (!(await this.connectivityService.isDeviceOnline())) {
      await this.presentOfflineToast();
    }

    await Network.addListener('networkStatusChange', async (networkStatus: ConnectionStatus) => {
      if (!networkStatus.connected) {
        await this.presentOfflineToast();
      }
    });
  }

  private async presentOfflineToast(): Promise<void> {
    // describeConnectionType() only ever returns a real "wifi"/"cellular" name, never the
    // literal "none"/"unknown" @capacitor/network can report (the latter on any browser without
    // the Network Information API — Safari and Firefox included) — see its own doc comment.
    const connectionType = await this.connectivityService.describeConnectionType();
    const message = connectionType
      ? `Your ${connectionType} network seems to be offline.`
      : 'Your network seems to be offline.';
    await this.toastService.presentToast('Network status', message, 'top', 'danger', 4000);
  }

  async handleMessage() {
    // Increment local counter when a notification is received in background/foreground
    await PushNotifications.addListener('pushNotificationReceived', () => {
      this.unreadNotificationCount$.next(this.unreadNotificationCount$.value + 1);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', async (notification: ActionPerformed) => {
      console.log('Push notification action performed', notification.actionId, JSON.stringify(notification?.notification?.data));
      const internalRoute = this.trustedResourceService.resolveInternalRoute(notification.notification?.data?.openUrl)
        ?? this.debtNotificationRouter.routeFor(notification.notification?.data);
      if (internalRoute) {
        await this.router.navigateByUrl(internalRoute);
      } else {
        const alert = await this.alertController.create({
          header: 'Notification 📨',
          message: this.trustedResourceService.sanitizeDisplayText(notification.notification?.data?.text),
          buttons: ['Okay'],
        });
        await alert.present();
      }
    });
  }

  private async navigateFromAppUrl(url: string | null | undefined): Promise<void> {
    const internalRoute = this.resolveInternalRouteFromAppUrl(url);
    if (!internalRoute) {
      return;
    }

    const tokenTimestamp = await Preferences.get({ key: '_tokenTimestamp' });
    if (!tokenTimestamp.value) {
      await this.router.navigate(['/signIn'], { queryParams: { returnUrl: internalRoute } });
      return;
    }

    await this.router.navigateByUrl(internalRoute);
  }

  private resolveInternalRouteFromAppUrl(url: string | null | undefined): string | null {
    const value = String(url ?? '').trim();
    if (!value) {
      return null;
    }

    const directRoute = this.trustedResourceService.resolveInternalRoute(value);
    if (directRoute) {
      return directRoute;
    }

    try {
      const parsed = new URL(value);
      const protocol = parsed.protocol.replace(/:$/, '');

      if (protocol === this.customDeepLinkScheme) {
        return this.buildInternalRoute(parsed.hostname, parsed.pathname, parsed.search, parsed.hash);
      }

      if ((protocol === 'https' || protocol === 'http')
        && this.universalLinkHosts.includes(parsed.hostname)
        && this.trustedResourceService.resolveNavigationUrl(value)) {
        return this.buildInternalRoute('', parsed.pathname, parsed.search, parsed.hash);
      }
    } catch {
      return null;
    }

    return null;
  }

  private buildInternalRoute(hostSegment: string, pathname: string, search: string, hash: string): string | null {
    const routePath = [hostSegment, pathname]
      .filter(Boolean)
      .join('/')
      .replace(/\/+/g, '/');
    const normalizedPath = routePath.startsWith('/') ? routePath : `/${routePath}`;
    const route = `${normalizedPath}${search}${hash}`;

    return this.trustedResourceService.resolveInternalRoute(route);
  }

  handlePwaNotificationMessage = async (event: MessageEvent | any) => {
    // Fires only for background pushes re-broadcast by firebase-messaging-sw.js — the service
    // worker already showed the single OS/tray notification, so the page must NOT create another
    // Notification here (that was the "displayed twice" bug). We only sync in-app state.
    const payload = event?.data?.notification ? event.data : event;
    const body = this.trustedResourceService.sanitizeDisplayText(payload?.notification?.body);

    this.unreadNotificationCount$.next(this.unreadNotificationCount$.value + 1);

    // Show one in-app alert only when the tab is actually visible; when it isn't, the SW's tray
    // notification is the sole (single) user-facing display and an alert would double it.
    if (document.visibilityState === 'visible') {
      await this.presentNotification(body);
    }
  };

  /** Handles the service worker's postMessage on notificationclick (background/tray tap) — navigates the already-open tab since the SW itself only focuses it. */
  handleServiceWorkerMessage = async (event: MessageEvent) => {
    if (event?.data?.type !== 'notification-click') {
      return;
    }
    const route = this.trustedResourceService.resolveInternalRoute(event.data.url);
    if (route) {
      await this.router.navigateByUrl(route);
    }
  };

  async presentNotification(message: string) {
    const alert = await this.alertController.create({
      header: 'New Notification 📨',
      message,
      buttons: ['Okay'],
    });
    await alert.present();
  }
}
